/**
 * Owner self-service sub-delegate management API — Feature 075 (plan 013,
 * Workstream C). All endpoints act on the CALLER's own workspace; the
 * backend rejects delegated callers (no sub-sub-delegation).
 */

import { apiClient } from '@/core/api/client';
import type { DelegationPermission } from '@/shared/components/sub-delegation';
import type {
  InviteDelegatePayload,
  InviteDelegateResponse,
  RevokeDelegateResponse,
  SubDelegateList,
  UpdateDelegatePermissionsResponse,
} from '../types/subDelegateManagement';

const BASE_PATH = '/workspace/sub-delegates';

export async function listSubDelegates(): Promise<SubDelegateList> {
  const response = await apiClient.get<SubDelegateList>(BASE_PATH);
  return response.data;
}

export async function inviteSubDelegate(
  payload: InviteDelegatePayload,
): Promise<InviteDelegateResponse> {
  const response = await apiClient.post<InviteDelegateResponse>(BASE_PATH, payload);
  return response.data;
}

export async function resendSubDelegateInvite(
  pendingId: string,
): Promise<InviteDelegateResponse> {
  const response = await apiClient.post<InviteDelegateResponse>(
    `${BASE_PATH}/${pendingId}/resend`,
  );
  return response.data;
}

export async function cancelSubDelegateInvite(pendingId: string): Promise<void> {
  await apiClient.delete(`${BASE_PATH}/${pendingId}`);
}

export async function revokeSubDelegate(
  relationshipId: string,
): Promise<RevokeDelegateResponse> {
  const response = await apiClient.delete<RevokeDelegateResponse>(
    `${BASE_PATH}/relationships/${relationshipId}`,
  );
  return response.data;
}

export async function updateSubDelegatePermissions(
  relationshipId: string,
  permissions: DelegationPermission[],
): Promise<UpdateDelegatePermissionsResponse> {
  const response = await apiClient.patch<UpdateDelegatePermissionsResponse>(
    `${BASE_PATH}/relationships/${relationshipId}/permissions`,
    { permissions },
  );
  return response.data;
}

export const subDelegateManagementApi = {
  listSubDelegates,
  inviteSubDelegate,
  resendSubDelegateInvite,
  cancelSubDelegateInvite,
  revokeSubDelegate,
  updateSubDelegatePermissions,
};
